<script setup lang="ts">
import { ref } from 'vue';
import AppIcon from './AppIcon.vue';
import type { IconName } from './icons.js';

/** A single yes/no question in place of window.confirm. Destructive questions render in red. */
export interface Confirmation {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
  readonly busyLabel?: string;
  /** What stays the same afterwards, so the decision is easy to make. */
  readonly detail?: string;
  readonly icon?: IconName;
  readonly danger?: boolean;
}
const props = defineProps<{ busy?: boolean; error?: string }>();
const emit = defineEmits<{ confirm: []; close: [] }>();
const dialog = ref<HTMLDialogElement>();
const state = ref<Confirmation>({ title: '', message: '', confirmLabel: 'Confirm' });

function open(confirmation: Confirmation) {
  state.value = confirmation;
  dialog.value?.showModal();
}
function close() {
  dialog.value?.close();
}
defineExpose({ open, close });
</script>

<template>
  <dialog
    ref="dialog"
    class="dialog dialog-compact confirm-dialog"
    aria-labelledby="confirm-title"
    aria-describedby="confirm-message"
    @close="emit('close')"
    @cancel="
      (event) => {
        if (props.busy) event.preventDefault();
      }
    "
  >
    <div class="dialog-body">
      <div class="confirm-head">
        <span class="confirm-icon" :data-tone="state.danger ? 'danger' : 'accent'">
          <AppIcon :name="state.icon ?? (state.danger ? 'trash' : 'question')" />
        </span>
        <div>
          <h2 id="confirm-title">{{ state.title }}</h2>
          <p id="confirm-message">{{ state.message }}</p>
        </div>
      </div>
      <p v-if="state.detail" class="hint confirm-detail">{{ state.detail }}</p>
      <div v-if="props.error" class="banner error" role="alert">
        <AppIcon name="warning" /><span>{{ props.error }}</span>
      </div>
      <footer class="dialog-actions">
        <button type="button" :disabled="props.busy" @click="close">Cancel</button>
        <button
          type="button"
          :class="state.danger ? 'danger' : 'primary'"
          :disabled="props.busy"
          @click="emit('confirm')"
        >
          {{ props.busy ? (state.busyLabel ?? 'Working…') : state.confirmLabel }}
        </button>
      </footer>
    </div>
  </dialog>
</template>
